window.loxaInsurance = {
  isInitializing: false,
  isUpdatingFromObserver: false,
  proxyUrl: "/apps/proxy-1",
  timeoutMs: 10000,
  intervalMs: 100,
  analytics: true,

  // custom logger
  log(...args) {
    if (typeof LoxaDev === "undefined") {
      return; // Silently return if LoxaDev is not available
    }

    // Combine all arguments into a single string
    const message = args
      .map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)
      )
      .join(" ");

    if (LoxaDev.debugMode) {
      const timestamp = new Date().toISOString().split("T")[1].split(".")[0]; // HH:MM:SS
      LoxaDev.log(`[${timestamp}] ${message}`);
    } else {
      LoxaDev.log(message);
    }
  },

  // debug logger
  debug(...args) {
    if (typeof LoxaDev === "undefined") {
      return; // Silently return if LoxaDev is not available
    }
    const timestamp = new Date().toISOString().split("T")[1].split(".")[0]; // HH:MM:SS

    // Combine all arguments into a single string
    const message = args
      .map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)
      )
      .join(" ");

    LoxaDev.debug(`[${timestamp}] ${message}`);
  },

  generateSessionId: function () {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  },

  generateImpressionGuid: function () {
    return crypto.randomUUID();
  },

  trackAnalytics: function (
    event,
    productInfo,
    interactionData,
    impressionGuid
  ) {
    const self = this;

    if (!self.analytics) {
      return;
    }

    try {
      const analyticsPayload = {
        event: event,
        sessionId: self.sessionId,
        impressionGuid: impressionGuid,
        productInfo: productInfo || {},
        interactionData: interactionData || {},
      };

      fetch(`${self.proxyUrl}/analytics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(analyticsPayload),
      }).catch((error) => {
        self.debug("Analytics request failed:", error);
      });
    } catch (error) {
      self.debug("Analytics tracking error:", error);
    }
  },

  async processInsuranceData(item, cartItemContainer, addBlock, includeBlock) {
    try {
      const insuranceData = await this.getInsuranceInformation(
        item.title,
        item.id,
        item.price
      );

      if (insuranceData.active && insuranceData.insurable) {
        // Generate and set impression-guid for cart insurance blocks
        if (!cartItemContainer.getAttribute("data-impression-guid")) {
          cartItemContainer.setAttribute(
            "data-impression-guid",
            this.generateImpressionGuid()
          );
        }

        const totalPrice = (
          insuranceData.insurance_price * item.quantity
        ).toFixed(2);
        const priceDisplay = addBlock.querySelector(".insurance-price-display");

        if (priceDisplay) {
          priceDisplay.textContent = totalPrice;
          this.debug(
            "Price display element text after:",
            priceDisplay.textContent
          );
        } else {
          this.debug("No price display element found in addBlock:", addBlock);
        }

        cartItemContainer.style.setProperty("display", "block", "important");
        if (includeBlock) {
          includeBlock.style.setProperty("display", "none", "important");
        }
        addBlock.style.setProperty("display", "block", "important");

        cartItemContainer.classList.remove("hidden", "d-none", "cart-hidden");
      } else {
        // Hide the entire insurance block if not insurable
        cartItemContainer.style.setProperty("display", "none", "important");
      }
    } catch (error) {
      this.debug("Error processing insurance data:", error);

      // Hide the entire insurance block on error
      cartItemContainer.style.setProperty("display", "none", "important");
    }
  },

  async getInsuranceInformation(productTitle, productId, productPrice) {
    try {
      // LoxaDev should already be available from init function
      if (typeof LoxaDev === "undefined") {
        this.debug("LoxaDev not available, returning insurable: false.");
        return {
          sku: productId,
          product_price: productPrice,
          product_title: productTitle,
          insurable: false,
        };
      }

      LoxaDev.customProxyUrl = this.proxyUrl;

      const insuranceData = await LoxaDev.setupInsurance(
        productId,
        productTitle,
        productPrice / 100
      );
      this.debug(insuranceData);

      if (
        insuranceData.insuranceInformation.insurable &&
        insuranceData.insuranceInformation.active
      ) {
        return {
          sku: insuranceData.insuranceInformation.sku,
          product_price: insuranceData.insuranceInformation.product_price,
          product_title: insuranceData.insuranceInformation.product_title,
          insurable: true,
          active: true,
          inclusive_insurance:
            insuranceData.insuranceInformation.inclusive_insurance,
          insurance_category:
            insuranceData.insuranceInformation.insurance_category,
          insurance_price: parseFloat(insuranceData.insuranceVariant.price),
          insurance_term: insuranceData.insuranceInformation.insurance_term,
          insurance_id: insuranceData.insuranceVariant.id,
        };
      } else {
        return {
          sku: productId,
          product_price: productPrice,
          product_title: productTitle,
          insurable: false,
        };
      }
    } catch (error) {
      this.debug("Error fetching insurance map:", error);
      return {
        sku: productId,
        product_price: productPrice,
        product_title: productTitle,
        insurable: false,
      };
    }
  },

  refreshCart(response) {
    this.debug("Refreshing cart");

    // Trigger theme cart update
    if (typeof window.roar !== "undefined") {
      if (window.shopping_cart_type === "sidebar") {
        // If using a sidebar cart, make sure it stays open
        if (!window.location.pathname.includes("/cart")) {
          window.roar.updateCartSidebar();
          if (!document.querySelector(".cart-open")) {
            const cartEl =
              document.querySelector("#cart_block") ||
              document.querySelector("#cart_block_mobile");
            if (cartEl) cartEl.click();
          }
        } else {
          window.roar.updateCart(response, true);
        }
      } else {
        // For other cart types
        window.roar.updateCart(response);
      }
    } else {
      // Fallback to page reload
      window.location.reload();
    }
  },

  async addProductAndInsuranceToCart(
    productId,
    productTitle,
    productPrice,
    insuranceId,
    insurancePrice,
    insuranceCategory,
    quantity = 1
  ) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: productId,
              quantity: quantity,
            },
            {
              id: insuranceId,
              quantity: quantity,
              properties: {
                _loxaFurnitureInsurance: "Included",
                _insurancePremium: insurancePrice,
                _insuranceCategory: insuranceCategory,
                "Insured Product": productTitle,
                "Sum Insured": `£${parseFloat(productPrice / 100)}`,
                _insuredProductId: productId,
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add products to cart");
      }

      const cartData = await response.json();
      this.refreshCart(cartData);
    } catch (error) {
      this.log("Error adding products to cart:", error);
    }
  },

  async addInsuranceToCart(insuranceData, item) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: insuranceData.insurance_id,
              quantity: item.quantity,
              properties: {
                _loxaFurnitureInsurance: "Included",
                _insurancePremium: insuranceData.insurance_price,
                _insuranceCategory: insuranceData.insurance_category,
                "Insured Product": item.title,
                "Sum Insured": `£${parseFloat(item.price / 100)}`,
                _insuredProductId: item.variant_id,
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add insurance to cart");
      }

      const responseData = await response.json();
      this.refreshCart(responseData);
      return responseData;
    } catch (error) {
      this.log("Error adding insurance to cart:", error);
      throw error;
    }
  },

  async initialiseCart() {
    if (this.isInitializing) {
      this.debug("Cart initialization already in progress, skipping");
      return;
    }

    this.isInitializing = true;
    this.debug("Initializing cart...");

    try {
      await new Promise((resolve) => setTimeout(resolve, 50));

      const response = await fetch("/cart.json");
      if (!response.ok) {
        throw new Error("Failed to fetch cart");
      }

      const cart = await response.json();
      this.debug("Cart initialized with items:", cart.items);

      // Check for orphaned insurance products (without insured products)
      const orphanedInsurance = cart.items.filter((item) => {
        if (item.properties?._insuredProductId) {
          // Check if the insured product exists in cart
          return !cart.items.some(
            (product) => product.variant_id == item.properties._insuredProductId
          );
        }
        return false;
      });

      // remove orphaned insurance
      if (orphanedInsurance.length > 0) {
        this.debug("Found orphaned insurance products:", orphanedInsurance);

        for (const item of orphanedInsurance) {
          try {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                id: item.key,
                quantity: 0,
              }),
            });
            this.debug("Removed orphaned insurance:", item.title);
          } catch (error) {
            this.log("Error removing orphaned insurance:", error);
          }
        }

        // Refresh cart after removing orphaned insurance
        const updatedCartResponse = await fetch("/cart.json");
        const updatedCart = await updatedCartResponse.json();
        this.refreshCart(updatedCart);
        return; // Exit early to let the refresh handle reinitialization
      }

      const insuredProducts = cart.items
        .filter((item) => item.properties?._insuredProductId)
        .reduce((acc, item) => {
          acc[item.properties._insuredProductId] = {
            ...item,
            cartKey: item.key,
          };
          return acc;
        }, {});

      this.debug("Found insurance items with keys:", insuredProducts);

      // Check quantity mismatch between products and insurance
      for (const productId in insuredProducts) {
        const insuranceItem = insuredProducts[productId];
        const productItem = cart.items.find(
          (item) => item.variant_id == productId
        );

        if (productItem && productItem.quantity !== insuranceItem.quantity) {
          this.debug("Quantity mismatch detected:", {
            product: {
              id: productItem.variant_id,
              title: productItem.title,
              quantity: productItem.quantity,
            },
            insurance: {
              id: insuranceItem.id,
              title: insuranceItem.title,
              quantity: insuranceItem.quantity,
            },
          });

          try {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                id: insuranceItem.cartKey,
                quantity: productItem.quantity,
              }),
            });
            this.debug(
              "Updated insurance quantity to match product:",
              productItem.quantity
            );

            // update cart
            const updatedCartResponse = await fetch("/cart.json");
            const updatedCart = await updatedCartResponse.json();
            this.refreshCart(updatedCart);
          } catch (error) {
            this.log("Error updating insurance quantity:", error);
          }
        }
      }

      // Process each cart item
      cart.items.forEach((item) => {
        const isInsured = !!insuredProducts[item.variant_id];
        const cartItemContainers = document.querySelectorAll(
          `[data-loxa-cart-variant-id="${item.variant_id}"]`
        );

        this.log(
          `Processing item: ${item.title}, isInsured: ${isInsured}, containers found: ${cartItemContainers.length}`
        );

        cartItemContainers.forEach((cartItemContainer) => {
          const includeBlock = cartItemContainer.querySelector(
            ".loxa-insurance-included"
          );
          const addBlock = cartItemContainer.querySelector(
            ".loxa-insurance-add"
          );
          const loadingBlock = cartItemContainer.querySelector(
            ".loxa-insurance-loading"
          );

          // Track current visible block state
          const includeBlockVisible =
            includeBlock && includeBlock.style.display !== "none";
          const addBlockVisible = addBlock && addBlock.style.display !== "none";

          // Loading spinner only shows during quantity changes, not initial load

          if (isInsured && includeBlock) {
            // Show included block for items that already have insurance
            const insuranceItem = insuredProducts[item.variant_id];
            const insurancePrice = (
              insuranceItem.properties._insurancePremium * item.quantity
            ).toFixed(2);
            const priceDisplay = includeBlock.querySelector(
              ".insurance-price-display"
            );
            if (priceDisplay) {
              priceDisplay.textContent = insurancePrice;
            }

            cartItemContainer.style.setProperty(
              "display",
              "block",
              "important"
            );
            // Hide loading block for initial load
            if (loadingBlock) {
              loadingBlock.style.setProperty("display", "none", "important");
            }
            includeBlock.style.setProperty("display", "block", "important");
            if (addBlock) {
              addBlock.style.setProperty("display", "none", "important");
            }

            cartItemContainer.classList.remove(
              "hidden",
              "d-none",
              "cart-hidden"
            );
          } else if (!item.title.toLowerCase().includes("product protection")) {
            // Show add block for items that don't have insurance yet
            (async () => {
              const insuranceData = await this.getInsuranceInformation(
                item.title,
                item.id,
                item.price
              );

              if (insuranceData.active && insuranceData.insurable) {
                // Generate and set impression-guid for cart insurance blocks
                if (!cartItemContainer.getAttribute("data-impression-guid")) {
                  cartItemContainer.setAttribute(
                    "data-impression-guid",
                    this.generateImpressionGuid()
                  );
                }

                const totalPrice = (
                  insuranceData.insurance_price * item.quantity
                ).toFixed(2);
                const priceDisplay = addBlock.querySelector(
                  ".insurance-price-display"
                );

                if (priceDisplay) {
                  priceDisplay.textContent = totalPrice;
                  this.debug(
                    "Price display element text after:",
                    priceDisplay.textContent
                  );
                } else {
                  this.debug(
                    "No price display element found in addBlock:",
                    addBlock
                  );
                }

                // Show the insurance block and add block, hide include block
                cartItemContainer.style.setProperty(
                  "display",
                  "block",
                  "important"
                );
                if (loadingBlock) {
                  loadingBlock.style.setProperty(
                    "display",
                    "none",
                    "important"
                  );
                }
                if (includeBlock) {
                  includeBlock.style.setProperty(
                    "display",
                    "none",
                    "important"
                  );
                }
                addBlock.style.setProperty("display", "block", "important");

                cartItemContainer.classList.remove(
                  "hidden",
                  "d-none",
                  "cart-hidden"
                );
              } else {
                // Hide the entire insurance block if not insurable
                cartItemContainer.style.setProperty(
                  "display",
                  "none",
                  "important"
                );
              }
            })();
          }
        });
      });

      return cart;
    } catch (error) {
      console.log("Error initializing cart:", error);
      this.log("Error initializing cart:", error);
    } finally {
      this.isInitializing = false;
    }
  },

  setupGlobalListeners() {
    document.addEventListener("click", async (event) => {
      // Handle add insurance clicks in cart
      if (event.target.closest(".loxa-insurance-add")) {
        await this.handleCartInsuranceClick(event);
      }

      // Handle add insurance clicks on product page
      if (event.target.closest(".loxa-insurance-block")) {
        await this.handleProductPageInsuranceClick(event);
      }

      // Handle insurance remove clicks
      if (event.target.matches(".loxa-remove")) {
        await this.handleInsuranceRemoveClick(event);
      }

      // Handle quantity button clicks
      if (event.target.closest(".qty-btn")) {
        this.handleQuantityButtonClick(event);
      }
    });
  },

  async handleCartInsuranceClick(event) {
    const addBlock = event.target.closest(".loxa-insurance-add");
    const cartItemContainer = addBlock.closest("[data-loxa-cart-variant-id]");

    if (cartItemContainer) {
      const variantId = cartItemContainer.getAttribute(
        "data-loxa-cart-variant-id"
      );
      const impressionGuid = cartItemContainer.getAttribute(
        "data-impression-guid"
      );
      const modal = document.getElementById("loxa-cart-modal");
      const backdrop = document.getElementById("loxa-cart-backdrop");

      this.trackAnalytics(
        "insurance_cart_clicked",
        {
          variantId: variantId,
        },
        {
          location: "cart",
          interactionType: "click",
        },
        impressionGuid
      );

      // Get insurance data from the cart item
      const cartRes = await fetch("/cart.json");
      const cart = await cartRes.json();
      const item = cart.items.find(
        (item) => item.variant_id.toString() === variantId
      );

      if (item) {
        const insuranceData = await this.getInsuranceInformation(
          item.title,
          item.id,
          item.price
        );

        if (insuranceData.active && insuranceData.insurable) {
          const totalPrice = (
            insuranceData.insurance_price * item.quantity
          ).toFixed(2);

          const yesBtn = modal.querySelector('[data-action="accept"]');
          if (yesBtn) {
            yesBtn.textContent = `Yes, add protection (+£${totalPrice})`;
          }

          modal.style.display = "block";
          backdrop.style.display = "block";

          this.trackAnalytics(
            "insurance_modal_shown",
            {
              productId: item.id,
              productTitle: item.title,
              productPrice: item.price,
              variantId: variantId,
            },
            {
              location: "cart",
              modalType: "cart_modal",
            },
            impressionGuid
          );

          // Set up close handlers - remove existing listeners first
          const closeBtn = modal.querySelector(".modal-close");
          const noBtn = modal.querySelector('[data-action="decline"]');

          // Remove existing listeners to prevent duplicates
          if (closeBtn._loxaCloseHandler) {
            closeBtn.removeEventListener("click", closeBtn._loxaCloseHandler);
          }
          if (noBtn._loxaDeclineHandler) {
            noBtn.removeEventListener("click", noBtn._loxaDeclineHandler);
          }
          if (backdrop._loxaBackdropHandler) {
            backdrop.removeEventListener(
              "click",
              backdrop._loxaBackdropHandler
            );
          }
          if (yesBtn._loxaAcceptHandler) {
            yesBtn.removeEventListener("click", yesBtn._loxaAcceptHandler);
          }

          function closeModal() {
            modal.style.display = "none";
            backdrop.style.display = "none";
          }

          // Create new handlers and store references
          closeBtn._loxaCloseHandler = () => {
            this.trackAnalytics(
              "insurance_modal_closed",
              {
                productId: item.id,
                productTitle: item.title,
                productPrice: item.price,
                variantId: variantId,
              },
              {
                location: "cart",
                modalType: "cart_modal",
              },
              impressionGuid
            );
            closeModal();
          };

          noBtn._loxaDeclineHandler = () => {
            this.trackAnalytics(
              "insurance_modal_declined",
              {
                productId: item.id,
                productTitle: item.title,
                productPrice: item.price,
                variantId: variantId,
              },
              {
                location: "cart",
                modalType: "cart_modal",
              },
              impressionGuid
            );
            closeModal();
          };

          backdrop._loxaBackdropHandler = () => {
            this.trackAnalytics(
              "insurance_modal_closed",
              {
                productId: item.id,
                productTitle: item.title,
                productPrice: item.price,
                variantId: variantId,
              },
              {
                location: "cart",
                modalType: "cart_modal",
              },
              impressionGuid
            );
            closeModal();
          };

          yesBtn._loxaAcceptHandler = async () => {
            this.trackAnalytics(
              "insurance_modal_accepted",
              {
                productId: item.id,
                productTitle: item.title,
                productPrice: item.price,
                variantId: variantId,
                insuranceId: insuranceData.insurance_id,
                insurancePrice: insuranceData.insurance_price,
                insuranceCategory: insuranceData.insurance_category,
              },
              {
                location: "cart",
                modalType: "cart_modal",
                quantity: item.quantity,
              },
              impressionGuid
            );

            try {
              await this.addInsuranceToCart(insuranceData, item);
              closeModal();
            } catch (error) {
              this.log("Error adding insurance to cart:", error);
            }
          };

          // Add the new listeners
          closeBtn.addEventListener("click", closeBtn._loxaCloseHandler);
          noBtn.addEventListener("click", noBtn._loxaDeclineHandler);
          backdrop.addEventListener("click", backdrop._loxaBackdropHandler);
          yesBtn.addEventListener("click", yesBtn._loxaAcceptHandler);
        }
      }
    }
  },

  async handleProductPageInsuranceClick(event) {
    const insuranceBlock = event.target.closest(".loxa-insurance-block");
    const modal = document.getElementById("loxa-product-modal");
    const backdrop = document.getElementById("loxa-product-backdrop");

    if (modal && backdrop) {
      const productTitle = insuranceBlock.getAttribute(
        "data-loxa-variant-title"
      );
      const productHandle = insuranceBlock.getAttribute(
        "data-loxa-product-handle"
      );
      const productPrice = insuranceBlock.getAttribute(
        "data-loxa-variant-price"
      );
      const productId = insuranceBlock.getAttribute("data-loxa-variant-id");
      const impressionGuid = insuranceBlock.getAttribute(
        "data-impression-guid"
      );

      this.trackAnalytics(
        "insurance_product_page_clicked",
        {
          productId: productId,
          productTitle: productTitle,
          productPrice: productPrice,
          productHandle: productHandle,
        },
        {
          location: "product_page",
          interactionType: "click",
        },
        impressionGuid
      );

      const insuranceData = await this.getInsuranceInformation(
        productTitle,
        productHandle,
        productPrice
      );

      if (insuranceData.active && insuranceData.insurable) {
        // Calculate total insurance price
        const quantity = parseInt(
          document.querySelector("#quantity_wanted")?.value || 1
        );
        const totalPrice = (insuranceData.insurance_price * quantity).toFixed(
          2
        );

        const yesBtn = modal.querySelector('[data-action="accept"]');
        if (yesBtn) {
          yesBtn.textContent = `Yes, add protection (+£${totalPrice})`;
        }

        modal.style.display = "block";
        backdrop.style.display = "block";

        this.trackAnalytics(
          "insurance_modal_shown",
          {
            productId: productId,
            productTitle: productTitle,
            productPrice: productPrice,
            productHandle: productHandle,
          },
          {
            location: "product_page",
            modalType: "product_modal",
          },
          impressionGuid
        );

        // Set up close handlers - remove existing listeners first
        const closeBtn = modal.querySelector(".modal-close");
        const noBtn = modal.querySelector('[data-action="decline"]');

        // Remove existing listeners to prevent duplicates
        if (closeBtn._loxaProductCloseHandler) {
          closeBtn.removeEventListener(
            "click",
            closeBtn._loxaProductCloseHandler
          );
        }
        if (noBtn._loxaProductDeclineHandler) {
          noBtn.removeEventListener("click", noBtn._loxaProductDeclineHandler);
        }
        if (backdrop._loxaProductBackdropHandler) {
          backdrop.removeEventListener(
            "click",
            backdrop._loxaProductBackdropHandler
          );
        }
        if (yesBtn._loxaProductAcceptHandler) {
          yesBtn.removeEventListener("click", yesBtn._loxaProductAcceptHandler);
        }

        function closeModal() {
          modal.style.display = "none";
          backdrop.style.display = "none";
        }

        // Create new handlers and store references
        closeBtn._loxaProductCloseHandler = () => {
          this.trackAnalytics(
            "insurance_modal_closed",
            {
              productId: productId,
              productTitle: productTitle,
              productPrice: productPrice,
              productHandle: productHandle,
            },
            {
              location: "product_page",
              modalType: "product_modal",
            },
            impressionGuid
          );
          closeModal();
        };

        noBtn._loxaProductDeclineHandler = () => {
          this.trackAnalytics(
            "insurance_modal_declined",
            {
              productId: productId,
              productTitle: productTitle,
              productPrice: productPrice,
              productHandle: productHandle,
            },
            {
              location: "product_page",
              modalType: "product_modal",
            },
            impressionGuid
          );
          closeModal();
        };

        backdrop._loxaProductBackdropHandler = () => {
          this.trackAnalytics(
            "insurance_modal_closed",
            {
              productId: productId,
              productTitle: productTitle,
              productPrice: productPrice,
              productHandle: productHandle,
            },
            {
              location: "product_page",
              modalType: "product_modal",
            },
            impressionGuid
          );
          closeModal();
        };

        yesBtn._loxaProductAcceptHandler = () => {
          this.trackAnalytics(
            "insurance_modal_accepted",
            {
              productId: productId,
              productTitle: productTitle,
              productPrice: productPrice,
              productHandle: productHandle,
              insuranceId: insuranceData.insurance_id,
              insurancePrice: insuranceData.insurance_price,
              insuranceCategory: insuranceData.insurance_category,
            },
            {
              location: "product_page",
              modalType: "product_modal",
              quantity: quantity,
            },
            impressionGuid
          );

          this.addProductAndInsuranceToCart(
            productId,
            productTitle,
            productPrice,
            insuranceData.insurance_id,
            insuranceData.insurance_price,
            insuranceData.insurance_category,
            quantity
          );
          closeModal();
        };

        // Add the new listeners
        closeBtn.addEventListener("click", closeBtn._loxaProductCloseHandler);
        noBtn.addEventListener("click", noBtn._loxaProductDeclineHandler);
        backdrop.addEventListener(
          "click",
          backdrop._loxaProductBackdropHandler
        );
        yesBtn.addEventListener("click", yesBtn._loxaProductAcceptHandler);
      }
    }
  },

  async handleInsuranceRemoveClick(event) {
    this.debug("Insurance remove button clicked");
    event.preventDefault();
    event.stopPropagation();

    const cartItemContainer = event.target.closest(
      "[data-loxa-cart-variant-id]"
    );
    if (!cartItemContainer) {
      this.debug("Could not find cart item container for insurance remove");
      return;
    }

    const variantId = cartItemContainer.getAttribute(
      "data-loxa-cart-variant-id"
    );
    const impressionGuid = cartItemContainer.getAttribute(
      "data-impression-guid"
    );
    this.debug("Insurance remove - variant ID:", variantId);

    this.trackAnalytics(
      "insurance_removed",
      {
        variantId: variantId,
      },
      {
        location: "cart",
        interactionType: "remove",
      },
      impressionGuid
    );

    this.debug("Fetching cart to find insurance items...");
    const cartRes = await fetch("/cart.json");
    const cart = await cartRes.json();
    this.debug("Current cart items:", cart.items);

    const insuredProducts = cart.items
      .filter((item) => item.title.toLowerCase().includes("product protection"))
      .reduce((acc, item) => {
        const insuredProductId = item.properties._insuredProductId;
        if (insuredProductId) {
          acc[insuredProductId] = item;
        }
        return acc;
      }, {});
    this.debug("Found insured products:", insuredProducts);

    const insuranceItem = insuredProducts[variantId];
    this.debug("Insurance item for variant", variantId, ":", insuranceItem);

    if (insuranceItem) {
      try {
        this.debug("Removing insurance...");
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: insuranceItem.key,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to remove insurance from cart");
        }

        const responseData = await response.json();
        this.debug("Insurance removed successfully, calling refreshCart");
        this.refreshCart(responseData);
      } catch (error) {
        this.log("Error removing insurance:", error);
      }
    } else {
      this.debug("No insurance item found for variant", variantId);
    }
  },

  handleQuantityButtonClick(event) {
    // this.initialiseCart();
    // Let the cart observer handle quantity changes to avoid duplicate processing
  },

  setupCartObserver() {
    // Debouncing variables
    let cartUpdateTimeout;
    const debounceMs = 1000; // Wait 1 second after last mutation

    // Watch for cart updates from theme and trigger insurance logic
    const cartObserver = new MutationObserver((mutations) => {
      if (this.isUpdatingFromObserver) {
        return;
      }

      let shouldTrigger = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          const target = mutation.target;
          if (
            target.closest(".cart-window") ||
            target.closest("#cart_block") ||
            target.closest(".cart-block") ||
            target.closest(".cart-window-body")
          ) {
            // Skip mutations caused by our own insurance updates
            if (
              !target.closest(".loxa-insurance-included") &&
              !target.closest(".loxa-insurance-add") &&
              !target.closest(".loxa-insurance-cart-wrapper") &&
              !target.closest(".loxa-cart-modal") &&
              !target.closest(".loxa-cart-backdrop") &&
              !target.closest(".insurance-price-display")
            ) {
              const addedNodes = Array.from(mutation.addedNodes);
              const removedNodes = Array.from(mutation.removedNodes);

              const hasCartItemChanges =
                addedNodes.some(
                  (node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    !node.closest(".loxa-insurance-cart-wrapper") &&
                    !node.closest(".cart-total-price") &&
                    !node.closest(".cart-drawer-total-line-price") &&
                    !node.closest(".insurance-price-display") &&
                    (node.classList.contains("cart-item") ||
                      node.querySelector(".cart-item") ||
                      node.getAttribute("data-variant-id") ||
                      node.querySelector("[data-variant-id]"))
                ) ||
                removedNodes.some(
                  (node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    !node.closest(".loxa-insurance-cart-wrapper") &&
                    !node.closest(".cart-total-price") &&
                    !node.closest(".cart-drawer-total-line-price") &&
                    !node.closest(".insurance-price-display") &&
                    (node.classList.contains("cart-item") ||
                      node.querySelector(".cart-item") ||
                      node.getAttribute("data-variant-id") ||
                      node.querySelector("[data-variant-id]"))
                );

              if (hasCartItemChanges) {
                shouldTrigger = true;
              }
            }
          }
        }
      });

      if (shouldTrigger) {
        this.debug("Cart items changed by theme, triggering insurance logic");
        this.debug("Mutations detected:", mutations.length);

        // Clear any existing timeout
        clearTimeout(cartUpdateTimeout);

        // Set flag before any cart operations
        this.isUpdatingFromObserver = true;

        // Debounce the cart update
        cartUpdateTimeout = setTimeout(async () => {
          try {
            await this.initialiseCart();
          } catch (error) {
            this.log("Error in debounced cart update:", error);
          } finally {
            // Reset flag only after all operations complete
            this.isUpdatingFromObserver = false;
          }
        }, debounceMs);
      }
    });

    const cartElements = document.querySelectorAll(
      ".cart-window, #cart_block, .cart-block, .cart-window-body, .cart-container"
    );
    cartElements.forEach((element) => {
      cartObserver.observe(element, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    });
  },

  async waitForLoxa(timeoutMs = this.timeoutMs, intervalMs = this.intervalMs) {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const check = () => {
        if (typeof LoxaDev !== "undefined") {
          this.debug("LoxaDev is available, proceeding with initialization");
          resolve(true);
        } else if (Date.now() - start >= timeoutMs) {
          this.debug("LoxaDev not available after timeout, proceeding anyway");
          resolve(false);
        } else {
          setTimeout(check, intervalMs);
        }
      };

      check();
    });
  },

  async init() {
    // Generate session ID
    this.sessionId = this.generateSessionId();

    // Wait for LoxaDev to be available
    const loxaReady = await this.waitForLoxa();

    if (loxaReady) {
      this.log("Protecting products");
      // set custom proxy url as Daals already has an app using /proxy
      LoxaDev.customProxyUrl = this.proxyUrl;

      // Initial cart setup
      this.initialiseCart();

      // Set up all global event listeners
      this.setupGlobalListeners();
      this.setupCartObserver();
      this.setupVariantListener();
    }
  },

  // Setup variant change listener for product pages
  setupVariantListener: function () {
    // Remove existing listeners to prevent duplicates
    if (this.variantChangeHandler) {
      document.removeEventListener("change", this.variantChangeHandler);
    }

    // Create new handler
    this.variantChangeHandler = (event) => {
      if (event.target.matches(".swatch-radio")) {
        console.log("Variant changed, updating insurance...");
        // Add delay to allow ShopifyAnalytics to update
        setTimeout(() => {
          this.resetProductPage();
        }, 200);
      }
    };

    // Add the listener
    document.addEventListener("change", this.variantChangeHandler);
  },

  // Reset product page insurance block
  resetProductPage: function () {
    const insuranceBlock = document.querySelector(".loxa-insurance-block");
    if (!insuranceBlock) return;

    // Get current variant from ShopifyAnalytics
    const selectedVariantId = window.ShopifyAnalytics?.meta?.selectedVariantId;
    const product = window.ShopifyAnalytics?.meta?.product;

    if (!selectedVariantId || !product || !product.variants) {
      insuranceBlock.style.display = "none";
      return;
    }

    const selectedVariant = product.variants.find(
      (variant) => variant.id == selectedVariantId
    );
    if (!selectedVariant) {
      insuranceBlock.style.display = "none";
      return;
    }

    // Update insurance block data attributes
    const variantTitle = selectedVariant.name || product.title;
    insuranceBlock.setAttribute("data-loxa-variant-id", selectedVariant.id);
    insuranceBlock.setAttribute("data-loxa-variant-title", variantTitle);
    insuranceBlock.setAttribute(
      "data-loxa-variant-price",
      selectedVariant.price
    );
    insuranceBlock.setAttribute("data-loxa-product-handle", selectedVariant.id);

    // Reinitialize insurance
    this.initializeProductInsurance(
      insuranceBlock,
      variantTitle,
      selectedVariant.id,
      selectedVariant.price
    );
  },

  // Initialize product insurance block
  initializeProductInsurance: async function (
    insuranceBlock,
    productTitle,
    productHandle,
    productPrice
  ) {
    try {
      const insuranceData = await this.getInsuranceInformation(
        productTitle,
        productHandle,
        productPrice
      );

      if (insuranceData.active && insuranceData.insurable) {
        // Generate and set impression-guid for product insurance blocks
        if (!insuranceBlock.getAttribute("data-impression-guid")) {
          insuranceBlock.setAttribute(
            "data-impression-guid",
            this.generateImpressionGuid()
          );
        }

        insuranceBlock.style.display = "flex";
      } else {
        insuranceBlock.style.display = "none";
      }
    } catch (error) {
      console.error("Error initializing product insurance:", error);
      insuranceBlock.style.display = "none";
    }
  },
};

// Initialize on page load
document.addEventListener("DOMContentLoaded", function () {
  window.loxaInsurance.init();
});
